import { useState, useEffect } from 'react';
import type { RoadmapCompanyConfig, GrantProgram } from '@/types/roadmap.types';
import { getCompany } from '@/data/companies/index';

const API_BASE = import.meta.env.VITE_API_URL;

/**
 * Merge local overrides on top of a D1 baseline config.
 *
 * - Programs: local programs replace D1 programs by `id`. Any D1 program
 *   not overridden locally is kept as-is.
 * - Top-level scalars/objects: local wins when defined.
 */
function mergeConfigs(
  d1: RoadmapCompanyConfig,
  local: RoadmapCompanyConfig,
): RoadmapCompanyConfig {
  const localProgramMap = new Map(local.programs.map((p) => [p.id, p]));

  const mergedPrograms: GrantProgram[] = d1.programs.map(
    (d1p) => localProgramMap.get(d1p.id) ?? d1p,
  );

  // Include any local-only programs not present in D1
  for (const lp of local.programs) {
    if (!d1.programs.some((d1p) => d1p.id === lp.id)) {
      mergedPrograms.push(lp);
    }
  }

  return {
    ...d1,
    ...local,
    programs: mergedPrograms,
  };
}

/**
 * Load a RoadmapCompanyConfig.
 *
 * Resolution order (when VITE_API_URL is set):
 *  1. Fetch the live D1 baseline from the Worker.
 *  2. If a local static config exists for this company, deep-merge local
 *     overrides on top of D1 — local programs replace D1 programs by id,
 *     top-level fields take the local value.
 *
 * When VITE_API_URL is unset, only the local static config is used.
 *
 * Add `?source=local` to force local-only (skip D1 entirely).
 * Add `?source=d1` to force D1-only (skip local overrides).
 */
export function useCompanyConfig(companyId: string | undefined) {
  const [config, setConfig] = useState<RoadmapCompanyConfig | undefined>();
  const [loading, setLoading] = useState(!!companyId);
  const [error, setError] = useState<string>();
  const [source, setSource] = useState<'d1' | 'local' | 'merged' | undefined>();

  useEffect(() => {
    if (!companyId) {
      setLoading(false);
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const forceSource = params.get('source'); // 'local' | 'd1' | null

    const localConfig = getCompany(companyId);

    // --- Force local only ---
    if (forceSource === 'local' || !API_BASE) {
      setConfig(localConfig);
      setSource(localConfig ? 'local' : undefined);
      setLoading(false);
      if (!localConfig) setError(`No static config for "${companyId}" and VITE_API_URL is not set`);
      return;
    }

    // --- Fetch D1 ---
    setLoading(true);
    setError(undefined);
    fetch(`${API_BASE}/api/roadmap/${companyId}`)
      .then((r) => {
        if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
        return r.json();
      })
      .then((data: { config: RoadmapCompanyConfig }) => {
        const d1Config = data.config;

        if (forceSource === 'd1' || !localConfig) {
          setConfig(d1Config);
          setSource('d1');
        } else {
          setConfig(mergeConfigs(d1Config, localConfig));
          setSource('merged');
        }
      })
      .catch((e) => {
        if (localConfig) {
          setConfig(localConfig);
          setSource('local');
        } else {
          setError(String(e));
        }
      })
      .finally(() => setLoading(false));
  }, [companyId]);

  return { config, loading, error, source };
}
